import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin, Globe } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { companyInfo } from '../mock/data';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Services', path: '/services' },
    { label: 'Solutions', path: '/solutions' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Why Us', path: '/why-us' },
    { label: 'Contact', path: '/contact' }
  ];

  const navLinkClass = ({ isActive }) =>
    [
      'relative inline-block font-medium transition-colors group pb-0.5',
      isActive ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
    ].join(' ');

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between py-2 text-sm">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs sm:text-sm">
              {companyInfo.contact.phone.map((phone) => (
                <a
                  key={phone}
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="flex items-center gap-1.5 hover:text-blue-200 transition-colors"
                >
                  <Phone className="h-3 w-3 shrink-0" />
                  <span>{phone}</span>
                </a>
              ))}
              <a
                href={`mailto:${companyInfo.contact.email}`}
                className="flex items-center gap-1.5 hover:text-blue-200 transition-colors min-w-0"
              >
                <Mail className="h-3 w-3 shrink-0" />
                <span className="break-all sm:break-normal">{companyInfo.contact.email}</span>
              </a>
              <a
                href={`https://${companyInfo.contact.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 hover:text-blue-200 transition-colors"
              >
                <Globe className="h-3 w-3 shrink-0" />
                <span>{companyInfo.contact.website}</span>
              </a>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <MapPin className="h-3 w-3" />
              <span className="hidden md:inline">Ahmedabad, India</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center space-x-3"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ad90301e-955a-4498-9a69-0fd44dadd032/artifacts/2nn1km20_Logo%20PNG%20%281%29.png" 
                alt="Nexalus Infotech Logo"
                className="h-12 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={navLinkClass}
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300" />
              </NavLink>
            ))}

          </nav>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-gray-700 hover:text-blue-600 transition-colors"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile: slide-in menu from the right */}
      <div
        className={`fixed inset-0 z-[100] lg:hidden ${
          mobileMenuOpen ? 'visible' : 'invisible pointer-events-none'
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ease-out ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
        <aside
          className={`absolute top-0 right-0 flex h-full w-[min(85vw,20rem)] flex-col bg-white shadow-2xl transition-transform duration-300 ease-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4">
            <span className="text-lg font-semibold text-gray-900">Menu</span>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="rounded-lg p-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-4">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 text-base transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-600 font-semibold'
                      : 'text-gray-800 hover:bg-blue-50 hover:text-blue-600'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
      </div>
    </header>
  );
};

export default Header;
